<script setup lang="ts">
import BaseButton from '@/components/Buttons/BaseButton.vue'
import { BaseCard } from '@/components/Cards'
import { StackLayout } from '@/components/Layouts'
import FullScreenLoader from '@/components/LoadingSpinner/FullScreenLoader.vue'
import TitleTypography from '@/components/Typography/TitleTypography.vue'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import store from '@/store'
import { ENetworkActions } from '@/store/networksStore/NetworkStateType'
import { EStoreModules } from '@/store/storeType'
import { EColors } from '@/types/constants/ColorValues'
import { type NetworkInviteModel } from '@/types/models/NetworkModel'
import { computed, onMounted } from 'vue'
import OrganizationCustomerCard from './components/OrganizationNetworkCard.vue'

type Props = {
    data: NetworkInviteModel[]
    total?: number
    currentPage: number
}
const props = defineProps<Props>()
type Emits = {
    (e: 'getReceivedInvites', page: number): void
}
const emit = defineEmits<Emits>()

const me = computed(() => store.state.authentication.me)

const handleDecline = (uuid: string) =>
    store
        .dispatch(`${EStoreModules.NETWORK}/${ENetworkActions.DECLINE_NETWORK_INVITE}`, {
            uuid,
            email: me.value.email
        })
        .then(() => emit('getReceivedInvites', 1))
const handleAccept = (uuid: string) =>
    store
        .dispatch(`${EStoreModules.NETWORK}/${ENetworkActions.VALIDATE_NETWORK_INVITE}`, {
            uuid,
            email: me.value.email
        })
        .then(() => emit('getReceivedInvites', 1))

const handleShowMore = () => emit('getReceivedInvites', props.currentPage + 1)

onMounted(() => emit('getReceivedInvites', props.currentPage))
</script>

<template>
    <FullScreenLoader :isLoading="!!store.state?.network?.isLoading" />
    <StackLayout direction="column" :gap="8">
        <TitleTypography
            :title="$t('organization.networks.invites.receivedInvites')"
            :type="EColors.PRIMARY" />
        <StackLayout v-if="data?.length" :gap="8" class="networks-list">
            <OrganizationCustomerCard
                v-for="(item, key) in data"
                :key="key"
                :prefix="$t('common.from')"
                :label="item.sender_name"
                class="user-card">
                <template #footer>
                    <BaseButton
                        :label="$t('organization.networks.invites.declineInvite')"
                        :type="EColors.DANGER"
                        class="full-width left-button"
                        @click="handleDecline(item.uuid)" />
                    <BaseButton
                        :label="$t('organization.networks.invites.acceptInvite')"
                        :type="EColors.SUCCESS"
                        class="full-width right-button"
                        @click="handleAccept(item.uuid)" />
                </template>
            </OrganizationCustomerCard>
            <BaseCard
                v-if="data.length < (total ?? 0)"
                @click="handleShowMore"
                class="network-card">
                <template #content>
                    <TypographyItem :label="$t('common.viewMore')" />
                </template>
            </BaseCard>
        </StackLayout>
        <TypographyItem
            v-else
            :label="$t('organization.networks.invites.noReceivedInviteCurrently')" />
    </StackLayout>
</template>

<style scoped lang="scss">
.network-card {
    border-radius: 100px;
    align-self: center;
    height: 100px;
    width: 100px;
    cursor: pointer;
    & :deep(.card-content) {
        text-align: center;
    }
}
.user-avatar {
    margin: auto;
}
.networks-list {
    flex-flow: wrap;
}
.user-card {
    & :deep(.card-header) {
        height: 36px;
    }
    & :deep(.card-content) {
        margin-top: 8px;
    }
}
</style>
